/**
 * Name and public links of the product in one place (daevio, on daevio.com): changing it here changes
 * the public pages. Payment links arrive with F15.
 */
export const BRAND = 'daevio';

/** Contact for removal requests of a report about a site (shown only when set). */
export const REMOVAL_EMAIL: string | undefined = import.meta.env.VITE_REMOVAL_EMAIL || undefined;

/** A finished analysis shown as an example on the public page (optional). */
export const SAMPLE_REPORT_SLUG: string | undefined = import.meta.env.VITE_SAMPLE_REPORT_SLUG || undefined;

/** Where the dashboard lives (sign in, projects). */
export const APP_PATH = '/app';

/** The public page about the script and the real visitors (shown only when they are on the public pages). */
export const REAL_VISITORS_PATH = '/real-visitors';

/** A link to that page saying where it was clicked, for the `real_visitors_page_view` event. */
export function realVisitorsLink(from: 'landing' | 'faq' | 'footer' | 'install'): string {
  return `${REAL_VISITORS_PATH}?from=${from}`;
}

/** A paid plan someone picked on the public prices: it rides along the sign-up so the app can take them to pay. */
export type PaidPlan = 'pro' | 'enterprise';

export function isPaidPlan(value: string | null): value is PaidPlan {
  return value === 'pro' || value === 'enterprise';
}

const PLAN_INTENT_KEY = 'daevio_plan_intent';
const PLAN_INTENT_MS = 24 * 3600 * 1000;

/**
 * The plan picked on the prices, kept in this browser for a day: sign-up (email code, Google, the workspace being
 * set up) may lose the `?plan=` on the way, the app still takes the new account to pay.
 */
export function rememberPlan(plan: PaidPlan): void {
  try {
    localStorage.setItem(PLAN_INTENT_KEY, JSON.stringify({ plan, at: Date.now() }));
  } catch {
    // storage off: the ?plan= in the address still works
  }
}

/** The plan picked on the prices in the last day, if any. */
export function rememberedPlan(): PaidPlan | null {
  try {
    const raw = localStorage.getItem(PLAN_INTENT_KEY);
    if (!raw) return null;
    const { plan, at } = JSON.parse(raw) as { plan: string; at: number };
    return isPaidPlan(plan) && Date.now() - at < PLAN_INTENT_MS ? plan : null;
  } catch {
    return null;
  }
}

export function forgetPlan(): void {
  try {
    localStorage.removeItem(PLAN_INTENT_KEY);
  } catch {
    // nothing stored
  }
}

/**
 * Sign-up page; `next` (an app path) is where the new account lands, e.g. saving an analysis, and `plan` the paid
 * plan picked on the prices (the account lands on `/app?plan=…`).
 */
export function signUpPath(next?: string, plan?: PaidPlan): string {
  const params = new URLSearchParams();
  if (next) params.set('next', next);
  if (plan) params.set('plan', plan);
  const query = params.toString();
  return query ? `${APP_PATH}/sign-up?${query}` : `${APP_PATH}/sign-up`;
}

/** Only app paths are accepted as a destination after signing up (no open redirect). */
export function safeNext(next: string | null): string | null {
  return next && next.startsWith(`${APP_PATH}/`) && !next.startsWith('//') ? next : null;
}

/** Where the sign-up page sends a new account: its `next`, else the app with the plan picked on the prices. */
export function signUpRedirect(params: URLSearchParams): string {
  const next = safeNext(params.get('next'));
  if (next) return next;
  const plan = params.get('plan');
  return isPaidPlan(plan) ? `${APP_PATH}?plan=${plan}` : APP_PATH;
}

/** Contact for Enterprise plans tailored to a team (link shown only when set). */
export const CONTACT_EMAIL: string | undefined = import.meta.env.VITE_CONTACT_EMAIL || undefined;

/** The answer to "is this like Hotjar or Clarity?", the same on the landing and on /real-visitors. */
export const HOTJAR_ANSWER =
  'Those show you what happened after real people struggled. daevio shows you before: AI visitors use your site first and tell you where they got stuck. When real customers arrive, the same script records them too, so you can check whether they hit the same blocks.';

/** Shown below the plans: what every plan has (the second one once real visitors are on the public pages). */
export const EVERY_PLAN_NOTE = 'Every plan includes an AI reading of your site, missions, replays with thoughts, a report you can download and a fix prompt.';
export const EVERY_PLAN_NOTE_REAL =
  'Every plan includes an AI reading of your site, missions, replays with thoughts, a report you can download, a fix prompt, and replays and heatmaps of your real visitors.';

/**
 * Plans on the public page (project doc "daevio — Piani e limiti", 22-26 October 2026): they differ in visits a
 * month, real sessions a month, sites, own personas and members only. `sessions` is listed after the sites. The limits themselves live in the admin settings; keep these
 * lines in step with them.
 */
export const PRICING = [
  {
    name: 'Free',
    sessions: '300 real sessions a month',
    price: '€0',
    note: 'free account',
    visits: '10 visits a month',
    items: ['1 site', 'All 30 ready-made personas', '1 persona of your own', 'Replays, report and fix prompt'],
    cta: 'Test my site',
  },
  {
    name: 'Pro',
    sessions: '3,000 real sessions a month',
    price: '€19',
    note: 'per month, cancel anytime',
    visits: '150 visits a month',
    items: ['3 sites', 'Unlimited personas of your own', '3 members', 'Founder price, locked forever'],
    cta: 'Start Pro',
    plan: 'pro',
  },
  {
    name: 'Enterprise',
    sessions: '15,000 real sessions a month',
    price: '€79',
    note: 'per month',
    visits: '600 visits a month',
    items: ['15 sites', 'Unlimited personas of your own', 'Unlimited members'],
    cta: 'Start Enterprise',
    plan: 'enterprise',
    custom: 'Need more? We tailor it to your team.',
  },
] as const;

/** Whether the landing shows the promo film (off: the section goes, nothing else changes). */
export const SHOW_PROMO_VIDEO = true;

/** The promo film on the landing (project doc "daevio — Video promo landing"): 1:34, subtitles burned in. */
export const PROMO_VIDEO = {
  src: '/video/daevio-spot.mp4',
  poster: '/video/daevio-spot-poster.jpg',
  duration: '1:34',
} as const;
